/**
 * TransformSpec v1：P1（数据集行）/ P2（设备报文）共用的可视化解析规格。
 *
 * 设计目标：
 * - 单一事实源：可视化编辑器产出 spec，编译器生成 P1 legacy parser / P2 JS。
 * - 安全：可视化模式只允许白名单操作与安全表达式，不执行任意 JS。
 * - 向后兼容：旧 parser / parse_script 不受影响。
 */

import { parseExpression, collectIdentifiers, compileExpression } from './expression';

export type TransformMode = 'rows' | 'object';

export type FieldType = 'number' | 'string' | 'boolean' | 'date' | 'json';

export type UnitKind = 'none' | 'cToF' | 'fToC' | 'scale';

export interface UnitRule {
  kind: UnitKind;
  /** kind=scale 时的乘数 */
  scale?: number;
  /** kind=scale 时的偏移（先乘后加） */
  offset?: number;
}

export interface FieldRule {
  /** 源路径：a.b[0].c（P1 为行内字段；P2 为报文路径） */
  from: string;
  /** 目标字段（简单标识符） */
  to: string;
  type?: FieldType;
  unit?: UnitRule;
  /** 小数位 0~6 */
  decimals?: number;
  /** 缺失默认值（undefined 表示不设置） */
  fallback?: unknown;
  /** 必填（缺失时 UI 告警；运行时仍按 fallback/undefined 处理） */
  required?: boolean;
  /** date/string 的格式（date 默认 YYYY-MM-DD HH:mm:ss；string 为后缀） */
  format?: string;
}

export interface ComputeRule {
  to: string;
  /** 安全表达式（白名单函数） */
  expr: string;
  type?: FieldType;
  decimals?: number;
}

export type ConditionOp =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'contains'
  | 'in'
  | 'exists';

export interface ConditionItem {
  field: string;
  op: ConditionOp;
  value?: unknown;
}

export interface ConditionGroup {
  logic: 'and' | 'or';
  items: Array<ConditionItem | ConditionGroup>;
}

export type TransformStep =
  | { op: 'map'; fields: FieldRule[] }
  | { op: 'filter'; cond: ConditionGroup }
  | { op: 'compute'; fields: ComputeRule[] }
  | { op: 'rename'; from: string; to: string }
  | { op: 'drop'; fields: string[] }
  | { op: 'limit'; count: number }
  | { op: 'js'; code: string; note?: string };

export interface TransformSpec {
  v: 1;
  mode: TransformMode;
  source?: { path?: string };
  steps: TransformStep[];
  /** P2 输出路由；缺省=所有字段进 points */
  output?: {
    points?: string[];
    io?: string[];
    network?: string[];
  };
}

export interface ValidateSpecOptions {
  /** 是否允许 js 步骤（仅代码模式/服务端可信场景） */
  allowJs?: boolean;
}

export interface ValidateSpecResult {
  ok: boolean;
  errors: string[];
  spec?: TransformSpec;
}

const FIELD_TYPES: FieldType[] = ['number', 'string', 'boolean', 'date', 'json'];
const UNIT_KINDS: UnitKind[] = ['none', 'cToF', 'fToC', 'scale'];
const CONDITION_OPS: ConditionOp[] = [
  'eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'contains', 'in', 'exists',
];
const IDENT_RE = /^[A-Za-z_$][A-Za-z0-9_$]*$/;
const MAX_SPEC_BYTES = 64 * 1024;

function isObj(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

function pushFieldErrors(errors: string[], f: FieldRule, prefix: string): void {
  const from = asString(f.from);
  const to = asString(f.to);
  if (!from) errors.push(`${prefix}：源路径不能为空`);
  if (!to) errors.push(`${prefix}：目标字段不能为空`);
  else if (!IDENT_RE.test(to)) errors.push(`${prefix}：目标字段「${to}」需为字母/下划线开头的标识符`);
  if (f.type !== undefined && !FIELD_TYPES.includes(f.type)) {
    errors.push(`${prefix}：类型「${String(f.type)}」不支持`);
  }
  if (f.decimals !== undefined) {
    if (!isFiniteNumber(f.decimals) || f.decimals < 0 || f.decimals > 6) {
      errors.push(`${prefix}：小数位需为 0~6`);
    }
  }
  if (f.unit !== undefined) {
    if (!isObj(f.unit)) {
      errors.push(`${prefix}：单位换算配置非法`);
    } else {
      if (!UNIT_KINDS.includes(f.unit.kind as UnitKind)) {
        errors.push(`${prefix}：单位换算「${String(f.unit.kind)}」不支持`);
      }
      if (f.unit.scale !== undefined && !isFiniteNumber(f.unit.scale)) {
        errors.push(`${prefix}：换算乘数需为数字`);
      }
      if (f.unit.offset !== undefined && !isFiniteNumber(f.unit.offset)) {
        errors.push(`${prefix}：换算偏移需为数字`);
      }
    }
  }
}

function pushConditionErrors(
  errors: string[],
  cond: ConditionGroup,
  allowed: Set<string>,
  prefix: string,
  depth = 0,
): void {
  if (!isObj(cond)) {
    errors.push(`${prefix}：过滤条件非法`);
    return;
  }
  if (cond.logic !== 'and' && cond.logic !== 'or') {
    errors.push(`${prefix}：过滤逻辑需为 and/or`);
  }
  if (!Array.isArray(cond.items) || cond.items.length === 0) {
    errors.push(`${prefix}：过滤条件不能为空`);
    return;
  }
  if (cond.items.length > 20) errors.push(`${prefix}：过滤条件过多（≤20）`);
  if (depth >= 3) {
    errors.push(`${prefix}：过滤条件嵌套过深（≤3 层）`);
    return;
  }
  for (const item of cond.items) {
    if (!isObj(item)) {
      errors.push(`${prefix}：过滤条件项非法`);
      continue;
    }
    if ((item as ConditionGroup).items !== undefined) {
      pushConditionErrors(errors, item as ConditionGroup, allowed, prefix, depth + 1);
      continue;
    }
    const it = item as unknown as ConditionItem;
    const field = asString(it.field);
    if (!field) errors.push(`${prefix}：过滤字段不能为空`);
    else if (!allowed.has(field)) errors.push(`${prefix}：过滤字段「${field}」未在字段映射中定义`);
    if (!CONDITION_OPS.includes(it.op)) {
      errors.push(`${prefix}：操作符「${String(it.op)}」不支持`);
    }
    if (it.op === 'in' && !Array.isArray(it.value)) {
      errors.push(`${prefix}：in 的值需为数组`);
    }
    if (it.op === 'exists' && it.value !== undefined) {
      errors.push(`${prefix}：exists 不需要值`);
    }
  }
}

/**
 * 校验并归一化 TransformSpec。返回的 spec 是深拷贝后的安全对象（即使有错也尽量返回）。
 */
export function validateTransformSpec(
  input: unknown,
  opts: ValidateSpecOptions = {},
): ValidateSpecResult {
  const errors: string[] = [];
  if (!isObj(input)) return { ok: false, errors: ['规格必须是对象'] };
  if (JSON.stringify(input).length > MAX_SPEC_BYTES) {
    return { ok: false, errors: [`规格过大（≤${MAX_SPEC_BYTES / 1024}KB）`] };
  }
  if (input.v !== 1) errors.push('规格版本必须为 1');
  const mode = input.mode;
  if (mode !== 'rows' && mode !== 'object') errors.push('mode 需为 rows 或 object');

  const source = isObj(input.source) ? input.source : undefined;
  if (source && source.path !== undefined && typeof source.path !== 'string') {
    errors.push('source.path 需为字符串');
  }
  if (source && typeof source.path === 'string' && source.path.length > 200) {
    errors.push('source.path 过长（≤200）');
  }

  const steps = Array.isArray(input.steps) ? input.steps : [];
  if (!Array.isArray(input.steps)) errors.push('steps 需为数组');
  if (steps.length > 20) errors.push('步骤过多（≤20）');

  const mapped = new Set<string>();
  const computed = new Set<string>();
  const output = isObj(input.output) ? input.output : undefined;

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    const prefix = `步骤 ${i + 1}`;
    if (!isObj(step)) {
      errors.push(`${prefix}：步骤非法`);
      continue;
    }
    switch (step.op) {
      case 'map': {
        const fields = Array.isArray(step.fields) ? step.fields : [];
        if (!Array.isArray(step.fields) || fields.length === 0) {
          errors.push(`${prefix}：字段映射不能为空`);
          break;
        }
        if (fields.length > 50) errors.push(`${prefix}：字段映射过多（≤50）`);
        for (const f of fields) {
          if (!isObj(f)) {
            errors.push(`${prefix}：字段映射项非法`);
            continue;
          }
          const fr = f as unknown as FieldRule;
          pushFieldErrors(errors, fr, prefix);
          const to = asString(fr.to);
          if (to) {
            if (mapped.has(to) || computed.has(to)) errors.push(`${prefix}：目标字段「${to}」重复`);
            mapped.add(to);
          }
        }
        break;
      }
      case 'compute': {
        const fields = Array.isArray(step.fields) ? step.fields : [];
        if (!Array.isArray(step.fields) || fields.length === 0) {
          errors.push(`${prefix}：计算字段不能为空`);
          break;
        }
        if (fields.length > 20) errors.push(`${prefix}：计算字段过多（≤20）`);
        for (const f of fields) {
          if (!isObj(f)) {
            errors.push(`${prefix}：计算字段项非法`);
            continue;
          }
          const cr = f as unknown as ComputeRule;
          const to = asString(cr.to);
          if (!to) errors.push(`${prefix}：计算字段名不能为空`);
          else if (!IDENT_RE.test(to)) errors.push(`${prefix}：计算字段「${to}」需为标识符`);
          else if (mapped.has(to) || computed.has(to)) errors.push(`${prefix}：目标字段「${to}」重复`);
          else computed.add(to);
          const expr = asString(cr.expr);
          if (!expr) errors.push(`${prefix}：计算表达式不能为空`);
          else if (expr.length > 500) errors.push(`${prefix}：计算表达式过长（≤500）`);
          if (cr.type !== undefined && !FIELD_TYPES.includes(cr.type)) {
            errors.push(`${prefix}：计算字段类型「${String(cr.type)}」不支持`);
          }
          if (cr.decimals !== undefined && (!isFiniteNumber(cr.decimals) || cr.decimals < 0 || cr.decimals > 6)) {
            errors.push(`${prefix}：计算字段小数位需为 0~6`);
          }
        }
        break;
      }
      case 'filter': {
        const cond = step.cond as unknown;
        if (!isObj(cond)) {
          errors.push(`${prefix}：过滤条件非法`);
          break;
        }
        pushConditionErrors(errors, cond as unknown as ConditionGroup, mapped, prefix);
        break;
      }
      case 'rename': {
        const from = asString(step.from);
        const to = asString(step.to);
        if (!from || !to) errors.push(`${prefix}：重命名字段不能为空`);
        else if (!IDENT_RE.test(to)) errors.push(`${prefix}：重命名目标「${to}」需为标识符`);
        break;
      }
      case 'drop': {
        const fields = Array.isArray(step.fields) ? step.fields : [];
        if (fields.length === 0) errors.push(`${prefix}：删除字段不能为空`);
        for (const f of fields) {
          if (typeof f !== 'string' || !IDENT_RE.test(f)) {
            errors.push(`${prefix}：删除字段名非法`);
          }
        }
        break;
      }
      case 'limit': {
        if (!isFiniteNumber(step.count) || step.count < 1 || step.count > 10000) {
          errors.push(`${prefix}：limit 需为 1~10000`);
        }
        break;
      }
      case 'js': {
        if (!opts.allowJs) errors.push(`${prefix}：可视化模式不支持 js 步骤`);
        if (typeof step.code !== 'string' || !step.code.trim()) errors.push(`${prefix}：js 代码不能为空`);
        else if (step.code.length > 5000) errors.push(`${prefix}：js 代码过长（≤5000）`);
        break;
      }
      default:
        errors.push(`${prefix}：操作「${String((step as any).op)}」不支持`);
    }
  }

  // 表达式语法/函数/字段校验
  const allowedForCompute = new Set<string>([...mapped, ...computed, '$index', '$row', '$meta']);
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    if (!isObj(step) || step.op !== 'compute') continue;
    const fields = Array.isArray(step.fields) ? step.fields : [];
    for (const f of fields) {
      if (!isObj(f)) continue;
      const cr = f as unknown as ComputeRule;
      const expr = asString(cr.expr);
      if (!expr) continue;
      try {
        const node = parseExpression(expr);
        const unknown = [...collectIdentifiers(node)].filter((n) => !allowedForCompute.has(n));
        if (unknown.length) errors.push(`计算字段「${asString(cr.to)}」：未知字段「${unknown[0]}」`);
        compileExpression(node, { ident: (n) => n, meta: {} });
      } catch (e: any) {
        errors.push(`计算字段「${asString(cr.to)}」：${e?.message || '表达式非法'}`);
      }
    }
  }

  if (output !== undefined) {
    for (const key of ['points', 'io', 'network'] as const) {
      const arr = output[key];
      if (arr === undefined) continue;
      if (!Array.isArray(arr)) {
        errors.push(`output.${key} 需为数组`);
        continue;
      }
      if (arr.length > 100) errors.push(`output.${key} 字段过多（≤100）`);
      for (const k of arr) {
        if (typeof k !== 'string' || !IDENT_RE.test(k)) {
          errors.push(`output.${key} 字段名非法`);
        } else if (!mapped.has(k) && !computed.has(k)) {
          errors.push(`output.${key}：字段「${k}」未在映射/计算中定义`);
        }
      }
    }
  }

  const spec = JSON.parse(JSON.stringify(input)) as TransformSpec;
  return { ok: errors.length === 0, errors, spec };
}
